import { IS_EYEJACK_WEBXR_POLYFILL } from "../utils/utils";
interface IFrameData {
  metadata?: any | null;
  imageBitmap?: ImageBitmap | null;
  image?: HTMLImageElement | null;
}

export interface WSCameraOptions {
  frameStartCallback: () => void | null;
  frameEndCallback: () => void | null;
}

export default class WSCamera {

  private options: WSCameraOptions | null = null;
  private cameraCanvas: HTMLCanvasElement;
  private cameraContextBitmap: ImageBitmapRenderingContext | null = null;
  private cameraContext2d: CanvasRenderingContext2D | null = null;

  private worker?: Worker | null = null;
  private frameQueue: IFrameData[] = [];
  private textDecoder = new TextDecoder();
  private cameraFrameTimeLast:number = 0;
  private cameraCanvasUpdated = false;

  private jpegScale: number = 0.25;
  private jpegQuality: number = 0.75;
  private jpegScaleUpdated: boolean = false;
  private jpegQualityUpdated: boolean = false;
  private supported : boolean = true;
  
  constructor(options?: WSCameraOptions) {
    this.supported = IS_EYEJACK_WEBXR_POLYFILL;
    this.cameraCanvas = <HTMLCanvasElement>( document.getElementById('ejx-camera-canvas') );

    if( !this.supported ) {
      // Delete the cameraCanvas element 
      if( this.cameraCanvas ) {
        this.cameraCanvas.remove();
      }
      return;
    }
    if( this.cameraCanvas ) {
      this.options = options || null;
      window.EJFrameStart = this.frameStart.bind(this);
      window.EJFrameEnd = this.frameEnd.bind(this);
    } else {
      throw new Error("WSCamera.constructor - ejx-camera-canvas not found in dom.");
    }
  }

  //---------------------------------------------------------- init / kill.
  public init() {
    if (!this.supported) {
      console.warn("WSCamera.init: not supported.");
      return;
    }
    this.initWebSocketWorker();
  }

  public kill() {
    this.killWebSocketWorker();
  }

  //---------------------------------------------------------- camera canvas.
  public getCameraCanvas(): HTMLCanvasElement {
    return this.cameraCanvas;
  }

  public getCameraCanvasUpdated(): boolean {
    const value = this.cameraCanvasUpdated;
    this.cameraCanvasUpdated = false;
    return value;
  }

  //---------------------------------------------------------- jpeg scale.
  public setJpegScale( value:number ) {
    this.jpegScale = value;
    if( this.worker ) {
      this.worker.postMessage({ type: "SET_JPEG_SCALE", value });
    }
  }

  public getJpegScale() {
    return this.jpegScale;
  }

  public getJpegScaleUpdated() {
    const value = this.jpegScaleUpdated;
    this.jpegScaleUpdated = false;
    return value;
  }

  //---------------------------------------------------------- jpeg quality.
  public setJpegQuality( value:number ) {
    this.jpegQuality = value;
    if( this.worker ) {
      this.worker.postMessage({ type: "SET_JPEG_QUALITY", value });
    }
  }

  public getJpegQuality() {
    return this.jpegQuality;
  }

  public getJpegQualityUpdated() {
    const value = this.jpegQualityUpdated;
    this.jpegQualityUpdated = false;
    return value;
  }

  //---------------------------------------------------------- web worker.
  private initWebSocketWorker() {
    if( this.worker ) {
      return;
    }
    this.worker = new Worker( "js/wsWorker.js" );
    this.worker.onmessage = (evt) => this.handleWorkerMessage(evt);
    this.worker.postMessage({ type: "INIT" });
  }

  private killWebSocketWorker() {
    if( this.worker ) {
      this.worker.postMessage({ type: "KILL" });
      this.worker.terminate();
      this.worker = null;
    }
  }

  private handleWorkerMessage(evt: MessageEvent) {
    const { type } = evt.data;
     if (type === "WS_OPEN") {
      console.log("[Main] WebSocket opened");
      const { jpegScale, jpegQuality } = evt.data;
      this.processWsOpenMessage(jpegScale, jpegQuality);
    } else if (type === "WS_ERROR") {
      console.error("[Main] WebSocket error:", evt.data.error);
    } else if (type === "WS_CLOSED") {
      console.log("[Main] WebSocket closed.");
    } else if (type === "FRAME_DATA") {
      const { frameBuffer, imageBitmap } = evt.data;
      this.processFrameDataMessage(frameBuffer, imageBitmap);
    }
  }

  private processWsOpenMessage( jpegScale:number, jpegQuality:number ) {
    if(jpegScale) {
      this.jpegScale = jpegScale;
      this.jpegScaleUpdated = true;
    }
    if(jpegQuality) {
      this.jpegQuality = jpegQuality;
      this.jpegQualityUpdated = true;
    }
  }

  private async processFrameDataMessage( frameBuffer:ArrayBuffer, imageBitmap:ImageBitmap ) {
    const frame: IFrameData = {};
    //
    const dataView = new DataView(frameBuffer);
    const jsonByteStart = 4;
    const jsonByteLength = dataView.getUint32(0, false);
    try {
      if (jsonByteStart + jsonByteLength > frameBuffer.byteLength) {
        throw new Error("Invalid JSON length, skipping frame.");
      }
      const jsonBytes = new Uint8Array(frameBuffer, jsonByteStart, jsonByteLength);
      const jsonString = this.textDecoder.decode(jsonBytes);
      const jsonMetadata = JSON.parse(jsonString);
      frame.metadata = jsonMetadata;
    } catch (error) {
      console.error(error);
      return;
    }

    if( imageBitmap ) { // already been created in worker.
      frame.imageBitmap = imageBitmap;
      this.frameQueue.push(frame);
      return;
    }

    const jpegByteStart = jsonByteStart + jsonByteLength;
    const jpegByteLength = frameBuffer.byteLength - jpegByteStart;
    try {
      if (jpegByteStart + jpegByteLength > frameBuffer.byteLength) {
        throw new Error("Invalid JPEG length, skipping frame.");
      }      
      const jpegBytes = new Uint8Array(frameBuffer, jpegByteStart, jpegByteLength);
      const blob = new Blob([jpegBytes], { type: "image/jpeg" });
      //
      const useImageBitmap = true;
      if(useImageBitmap) {
        frame.imageBitmap = await createImageBitmap(blob);
      } else {
        frame.image = await this.createImage(blob);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.frameQueue.push(frame);
    }
  }

  private async createImage(blob:Blob) {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const image = new Image();
      image.src = url;
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      }
      image.onerror = (error) => {
        URL.revokeObjectURL(url);
        reject(error);
      };
    });
  }

  //---------------------------------------------------------- sync frame.
  private async frameStart() {
    // starting with the most recent frames in the frameQueue,
    // find the first frame with valid metadata and image data,
    // then remove that frame + all previous frames from the queue.
    // any frames after the valid frame are left in the queue, 
    // as they are still loading their image.

    let frame: IFrameData | null = null;
    for( let i=this.frameQueue.length - 1; i>=0; i-- ) {
      const frameI = this.frameQueue[i];
      if( frame ) { // frame already found. kill all previous frames.
        this.frameDataKill( frameI );
        this.frameQueue.splice(i, 1);
      } else {
        const valid = frameI && frameI.metadata && (frameI.imageBitmap || frameI.image);
        if( valid ) {
          frame = frameI;
          this.frameQueue.splice(i, 1); // remove frame from queue.
        }
      }
    }
    if( !frame ) {
      return;
    }
    if( frame.metadata.cameraData.timestamp === this.cameraFrameTimeLast ) {
      return; // duplicate frame - skip.
    } else {
      this.cameraFrameTimeLast = frame.metadata.cameraData.timestamp;
    }
    if(this.options) {
      if(this.options.frameStartCallback) {
        this.options.frameStartCallback();
      }
    }
    if( window.EJUpdateCameraData ) {
      window.EJUpdateCameraData(frame.metadata.cameraData);
    }
    if( frame.imageBitmap ) { 
      this.updateCameraCanvas( frame.imageBitmap );
    } else if( frame.image ) {
      this.updateCameraCanvas( frame.image );
    }
    this.frameDataKill( frame );
  }

  private frameDataKill( frame: IFrameData ) {
    if( frame.metadata ) {
      frame.metadata = null;
    }
    if( frame.imageBitmap ) {
      frame.imageBitmap.close();
      frame.imageBitmap = null;
    }
    if( frame.image ) {
      frame.image = null;
    }
  }

  private async frameEnd() {
    if(this.options) {
      if(this.options.frameEndCallback) {
        this.options.frameEndCallback();
      }
    }
  }

  private updateCameraCanvas( image: HTMLImageElement | ImageBitmap ) {
    const isImage = image instanceof HTMLImageElement;
    const isImageBitmap = image instanceof ImageBitmap;
    const imageW = isImage ? image.naturalWidth : image.width;
    const imageH = isImage ? image.naturalHeight : image.height;
    const sizeChanged = (this.cameraCanvas.width !== imageW) || (this.cameraCanvas.height !== imageH);
    if( sizeChanged ) {
      this.cameraCanvas.width = imageW;
      this.cameraCanvas.height = imageH;
    }
    const useBitmaprendererContext = true;
    if( useBitmaprendererContext && isImageBitmap ) {
      // transfer imageBitmap to canvas using bitmaprenderer context.
      // this is faster than drawing the image to the canvas using 2d context.
      const ctx = this.getCameraContextBitmap();
      ctx.transferFromImageBitmap(image);
    } else {
      const ctx = this.getCameraContext2d();
      ctx.drawImage(image, 0, 0, imageW, imageH);
      //
      const tintBlue = false;
      if( tintBlue ) {
        ctx.globalAlpha = 0.3;
        ctx.fillStyle = 'blue';
        ctx.fillRect(0, 0, imageW, imageH);
        ctx.globalAlpha = 1.0;
      }
    }
    this.cameraCanvasUpdated = true;
  }

  private getCameraContextBitmap(): ImageBitmapRenderingContext {
    if (!this.cameraContextBitmap) {
      this.cameraContextBitmap = this.cameraCanvas.getContext('bitmaprenderer');
    }
    return this.cameraContextBitmap!;
  }
  
  private getCameraContext2d(): CanvasRenderingContext2D {
    if (!this.cameraContext2d) {
      this.cameraContext2d = this.cameraCanvas.getContext('2d');
    }
    return this.cameraContext2d!;
  }  
}
